import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Globe, Shield, Banknote, BellRing, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";
import ImageUploader from "@/components/ImageUploader";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBanners, setUploadingBanners] = useState([]);

  const loadSettings = async () => {
    try {
      const response = await api.get("/admin/settings");
      const hero_banners = Array.isArray(response.data.hero_banners)
        ? response.data.hero_banners.map((banner, index) => ({
          ...banner,
          id: banner.id || `existing-${index}`,
        }))
        : [];
      setSettings({ ...response.data, hero_banners });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("تم حفظ إعدادات النظام بنجاح");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const updateBanners = (hero_banners) => setSettings((current) => ({ ...current, hero_banners }));

  const addBanner = () => {
    updateBanners([...(settings?.hero_banners || []), {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      image_path: "",
      enabled: true,
    }]);
  };

  const moveBanner = (index, direction) => {
    const banners = [...(settings?.hero_banners || [])];
    const target = index + direction;
    if (target < 0 || target >= banners.length) return;
    [banners[index], banners[target]] = [banners[target], banners[index]];
    updateBanners(banners);
  };

  if (loading) {
    return (
      <AdminLayout title="إعدادات النظام" subtitle="التحكم بالمعلومات العامة وشروط التوصيل">
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="إعدادات النظام العامة" subtitle="تخصيص اسم المنصة، الإعلانات الشريطية، رسوم التوصيل الافتراضية، ووضع الصيانة">
      <form onSubmit={handleSave} className="max-w-3xl space-y-6">
        <Card className="border border-border/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BellRing className="w-4 h-4 text-primary" />
              شرائح صور الهيرو والإعلانات
            </CardTitle>
            <CardDescription>أضف تصاميم إعلانية كصور فقط؛ ستظهر بالتتابع أعلى الصفحة الرئيسية من دون نصوص مضافة فوقها.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(settings?.hero_banners || []).map((banner, index) => (
              <div key={banner.id || index} className="rounded-2xl border border-border p-3 sm:p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold">الشريحة {index + 1}</span>
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={banner.enabled !== false}
                      onCheckedChange={(enabled) => updateBanners(settings.hero_banners.map((item, itemIndex) => itemIndex === index ? { ...item, enabled } : item))}
                      aria-label={`تفعيل الشريحة ${index + 1}`}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => moveBanner(index, -1)} disabled={index === 0} aria-label="تحريك الشريحة للأعلى">
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => moveBanner(index, 1)} disabled={index === settings.hero_banners.length - 1} aria-label="تحريك الشريحة للأسفل">
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => updateBanners(settings.hero_banners.filter((_, itemIndex) => itemIndex !== index))} disabled={uploadingBanners.includes(banner.id)} aria-label={`حذف الشريحة ${index + 1}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <ImageUploader
                  value={banner.image_path}
                  onChange={(image_path) => updateBanners(settings.hero_banners.map((item, itemIndex) => itemIndex === index ? { ...item, image_path } : item))}
                  onUploadingChange={(uploading) => setUploadingBanners((current) => uploading
                    ? current.includes(banner.id) ? current : [...current, banner.id]
                    : current.filter((id) => id !== banner.id))}
                  testid={`hero-banner-${index}`}
                  aspect="video"
                  hint="يفضل رفع تصميم عريض يناسب مساحة الهيرو."
                />
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addBanner} className="w-full gap-2">
              <Plus className="h-4 w-4" />
              إضافة شريحة صورة
            </Button>
          </CardContent>
        </Card>

        <Card className="border border-border/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              هوية الموقع والتواصل
            </CardTitle>
            <CardDescription>البيانات التي تظهر للزبائن في واجهة التطبيق</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="set_name">اسم التطبيق / المنصة *</Label>
              <Input
                id="set_name"
                value={settings?.site_name || ""}
                onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                placeholder="تطبيق كفرنبل للتوصيل"
                required
              />
            </div>
            <div>
              <Label htmlFor="set_ann">رسالة ترحيبية نصية مستقلة</Label>
              <Input
                id="set_ann"
                value={settings?.announcement || ""}
                onChange={(e) => setSettings({ ...settings, announcement: e.target.value })}
                placeholder="أهلاً بكم في كفرنبل أوردر - التوصيل الأسرع حتى باب منزلك"
              />
              <p className="text-[11px] text-muted-foreground mt-1">هذا الإعداد منفصل عن شرائح الهيرو ولا يظهر فوق الصور.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="set_phone">رقم هاتف الدعم الفني / الشكاوى</Label>
                <Input
                  id="set_phone"
                  value={settings?.support_phone || ""}
                  onChange={(e) => setSettings({ ...settings, support_phone: e.target.value })}
                  placeholder="+963900000000"
                  dir="ltr"
                />
              </div>
              <div>
                <Label htmlFor="set_currency">رمز العملة المعتمد</Label>
                <Input
                  id="set_currency"
                  value={settings?.currency_symbol || "ل.س"}
                  onChange={(e) => setSettings({ ...settings, currency_symbol: e.target.value })}
                  placeholder="ل.س"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="w-4 h-4 text-primary" />
              إعدادات التوصيل الافتراضية
            </CardTitle>
            <CardDescription>القيم المعتمدة تلقائياً عند إنشاء المتاجر الجديدة</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="set_fee">أجرة التوصيل الافتراضية (ل.س)</Label>
              <Input
                id="set_fee"
                type="number"
                value={settings?.default_delivery_fee ?? 3000}
                onChange={(e) => setSettings({ ...settings, default_delivery_fee: Number(e.target.value) })}
                step={500}
                min={0}
              />
              <p className="text-[11px] text-muted-foreground mt-1">يتم اعتمادها تلقائياً عند إضافة متجر جديد ما لم يتم تخصيص رسوم أخرى له.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              حالة النظام والصيانة
            </CardTitle>
            <CardDescription>التحكم بفتح وإغلاق الطلبات على مستوى المنصة</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/20">
              <div className="space-y-0.5">
                <Label htmlFor="set_maint" className="text-sm font-semibold">تفعيل وضع الصيانة</Label>
                <p className="text-xs text-muted-foreground">عند التفعيل، يتم تنبيه الزبائن بأن استقبال الطلبات متوقف مؤقتاً لأعمال الصيانة.</p>
              </div>
              <Switch
                id="set_maint"
                checked={Boolean(settings?.maintenance_mode)}
                onCheckedChange={(checked) => setSettings({ ...settings, maintenance_mode: checked })}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={saving || uploadingBanners.length > 0} className="px-6 gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {uploadingBanners.length > 0 ? "انتظر اكتمال رفع الصور" : "حفظ كافة التغييرات"}
          </Button>
        </div>
      </form>
    </AdminLayout>
  );
}
